import React, { forwardRef, useCallback, useContext, useState } from 'react'
import { StyleSheet, Text, TextInput, TextInputProps as RNTextInputProps, View } from 'react-native'
import Loader from '../../loader'
import { ThemeContext } from '../../context/ThemeContext'
import { textStyles } from '../../../style'
import { Ionicons } from '@expo/vector-icons'

const LABEL_SPACE = 4

type SearchBarProps = {
  value: string
  onChange: (value: string) => void
  label?: string
  placeholder?: string
  textInputProps?: RNTextInputProps
  required?: boolean
  loading?: boolean
  hideLoading?: boolean
  search: (term: string) => void
}

export const ThemedSearchBar = forwardRef<TextInput, SearchBarProps>((props: SearchBarProps, ref) => {
  const theme = useContext(ThemeContext)
  const { primary, borderColor, textMain, placeholder } = theme.theme.colors

  const [isFocused, setIsFocused] = useState(false)

  const leftIconMargin = 10 + (props.textInputProps?.style?.marginLeft || props.textInputProps?.style?.marginHorizontal || 0)
  const rightIconMargin = 10 + (props.textInputProps?.style?.marginRight || props.textInputProps?.style?.marginHorizontal || 0)

  const debounce = (func, delay) => {
    let timeoutId
    return (...args) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        func.apply(this, args)
      }, delay)
    }
  }

  const debounceSearch = useCallback(
    debounce((term: string) => {
      props.search(term)
    }, 500),
    []
  )

  return (
    <View>
      {props.label && (
        <Text style={[textStyles.label, { color: isFocused ? primary : textMain, marginBottom: LABEL_SPACE }]}>
          {props.label}
          {props.required && <Text style={[textStyles.label, { color: 'red' }]}> *</Text>}
        </Text>
      )}
      <View>
        <TextInput
          ref={ref}
          placeholder={props.placeholder}
          placeholderTextColor={placeholder}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          value={props.value}
          onChangeText={(text) => {
            props.onChange(text)
            debounceSearch(text)
          }}
          {...props.textInputProps}
          style={[
            textStyles.body,
            styles.searchBar,
            {
              borderColor: isFocused ? primary : borderColor,
              color: textMain,
            },
            props.textInputProps?.style,
          ]}
          returnKeyType="done"
        />
        <View style={{ position: 'absolute', left: leftIconMargin, top: 10 }}>
          <Ionicons name="search" size={20} color={isFocused ? primary : borderColor} />
        </View>
        {props.loading && (
          <View style={{ position: 'absolute', right: rightIconMargin, top: 10 }}>
            <Loader size="small" />
          </View>
        )}
      </View>
    </View>
  )
})

const styles = StyleSheet.create({
  searchBar: {
    height: 42,
    borderRadius: 10,
    paddingLeft: 40,
    paddingRight: 40,
    paddingVertical: 10,
    borderWidth: 1,
    outlineWidth: 0,
  },
})


